/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable, Signal, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ORGANIZATION_WAIT_MS, UserAuthState } from '@app/app/user-auth/store/user-auth.state';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { map, shareReplay, timeout } from 'rxjs/operators';
import { ContentHelper } from '../helpers/content-helper';

/**
 * The single gate for the LMS mode, see `UserAuthState.ltiMyHubActive`. Off until the organisation has loaded and the
 * launch is known, and for good if the organisation fails to load.
 */
@Injectable({ providedIn: 'root' })
export class LtiMyHubService {
  /** Waits for the organisation, so that it can't answer before the gate has settled. Off if it never loads, and never waits outside an iframe. */
  readonly active$: Observable<boolean>;
  readonly active: Signal<boolean>;
  /** An iframe that isn't the LMS mode, so the frameless embed the app's iframe branches were written for. */
  readonly embedWithoutNav = computed(() => ContentHelper.isFrameMode() && !this.active());

  constructor(store: Store) {
    this.active$ = !ContentHelper.isFrameMode()
      ? of(false)
      : UserAuthState.organizationSettled$(store).pipe(
          map(() => store.selectSnapshot(UserAuthState.ltiMyHubActive)),
          timeout({ first: ORGANIZATION_WAIT_MS, with: () => of(false) }),
          shareReplay(1),
        );
    // Not from `active$`: the organisation can load before a launch has found out it is a global one.
    this.active = toSignal(store.select(UserAuthState.ltiMyHubActive), { initialValue: false });
  }
}
